import {
  Badge,
  Button,
  Checkbox,
  ErrorBanner,
  ActionBar,
} from "frontend-kit/components/index.js";
import { getChainApp, getWallet } from "./chain-app.js";
import {
  connectedMoonlightAccount,
  luxToDusk,
  readParticipationBondState,
} from "./chain-config.js";
import { renderParticipantPanel } from "./participant-panel.js";
import { isParticipantReady, loadParticipant } from "./participant-store.js";
import { loadTradingKey } from "./trading-key-store.js";
import { duskAmountNode } from "./dusk-amount.js";
import { icon } from "./icons.js";
import { isAutoRetryEnabled, setAutoRetryEnabled } from "./trade-settings.js";
import { showGlobalTx, trackTx } from "./tx-status.js";
import { base58ToBytes } from "./trader-crypto.js";
import { SETTINGS_EXPLAINERS } from "./guide-copy.js";
import { renderSettingsSecurityPanel } from "./settings-security-panel.js";
import {
  DEFAULT_SETTINGS_SECTION,
  SETTINGS_SECTIONS,
  settingsHash,
  settingsSectionLabel,
} from "./settings-sections.js";

const BOND_CUTOVER_MSG =
  "Creator participation bond is not live until Phase G cutover - predictionMarket Phase-2 bond methods and predictionMarketData (data contract + driver) must be wired in contracts.testnet.json.";

function bondUnavailableMessage(err) {
  const msg = err?.message || String(err);
  if (
    /participation_bond|predictionMarketData|Phase G|not configured|not live until/i.test(
      msg
    )
  ) {
    return BOND_CUTOVER_MSG;
  }
  return msg;
}

function walletAuthorized(wallet) {
  const state = wallet?.state;
  return Boolean(state?.authorized && state.profiles?.length);
}

function moonlightPkBytesFromWallet(wallet) {
  const account =
    wallet?.state?.selectedAddress || connectedMoonlightAccount(wallet);
  if (!account) return null;
  const bytes = base58ToBytes(account);
  if (bytes.length !== 96) {
    throw new Error("Connected Moonlight public key must be 96 bytes");
  }
  return bytes;
}

function statusLabel(participant, ready) {
  if (ready) return "Ready to trade";
  if (!participant) return "Not started";
  if (participant.status === "verified") return "Verified";
  return "Pending proof";
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Paint Status strip into `host` from current localStorage (no remount). */
function paintStatus(host) {
  const participant = loadParticipant();
  const key = loadTradingKey();
  const ready = isParticipantReady(participant) && Boolean(key?.commitment_hex);

  const nodes = [];

  const badgeRow = document.createElement("div");
  badgeRow.className = "state-row";
  badgeRow.append(
    Badge({
      variant: ready ? "success" : participant ? "pending" : "neutral",
      label: statusLabel(participant, ready),
    })
  );
  nodes.push(badgeRow);

  if (participant?.participant_id) {
    const idRow = document.createElement("div");
    idRow.className = "settings-id-row";
    const code = document.createElement("code");
    code.textContent = participant.participant_id;
    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "copy-icon-btn";
    copyBtn.title = "Copy participant id";
    copyBtn.setAttribute("aria-label", "Copy participant id");
    copyBtn.append(icon("copy", { size: 16 }));
    copyBtn.addEventListener("click", async () => {
      const ok = await copyText(participant.participant_id);
      copyBtn.title = ok ? "Copied" : "Copy failed";
    });
    idRow.append(code, copyBtn);
    nodes.push(idRow);
  }

  const meta = document.createElement("p");
  meta.className = "hint";
  const bits = [];
  if (participant?.moonlight_account) {
    bits.push(`Wallet ${participant.moonlight_account.slice(0, 16)}…`);
  }
  if (key?.commitment_hex) {
    bits.push(`Commitment ${key.commitment_hex.slice(0, 16)}…`);
  }
  meta.textContent = bits.length ? bits.join(" · ") : "No wallet or trading key linked yet.";
  nodes.push(meta);

  host.replaceChildren(...nodes);
  return ready;
}

/**
 * Creator participation bond - Moonlight public sender (not trading-key auth).
 *
 * @param {{ wallet: object, log?: Function }} opts
 */
function renderParticipationBondSection({ wallet, log }) {
  const sec = document.createElement("section");
  sec.className = "settings-section dt-card";

  const h2 = document.createElement("h2");
  h2.textContent = "Creator participation bond";
  sec.append(h2);

  sec.append(
    Object.assign(document.createElement("p"), {
      className: "hint",
      textContent:
        "Standing bond for addresses with CREATE_MARKET right (from achievement NFT). Pulled from your Moonlight wallet balance - not trading collateral.",
    })
  );

  const privileged = document.createElement("p");
  privileged.className = "hint";
  privileged.textContent =
    "Contract owner and create-admin wallets skip the bond when creating events.";
  sec.append(privileged);

  const amountsHost = document.createElement("div");
  amountsHost.className = "settings-bond-amounts";
  sec.append(amountsHost);

  const feedback = document.createElement("div");
  feedback.className = "settings-bond-feedback";
  sec.append(feedback);

  const actionsHost = document.createElement("div");
  actionsHost.className = "settings-bond-actions";
  sec.append(actionsHost);

  let loading = true;
  let busy = false;
  let bondErr = null;
  let required = 0n;
  let posted = 0n;
  let walletLux = null;
  let cutover = false;

  function paintAmounts() {
    amountsHost.replaceChildren();
    if (loading) {
      amountsHost.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading bond state…",
        })
      );
      return;
    }
    if (bondErr || cutover) {
      amountsHost.append(ErrorBanner({ message: bondErr || BOND_CUTOVER_MSG }));
      return;
    }

    const row = document.createElement("div");
    row.className = "settings-bond-row";

    const requiredP = document.createElement("p");
    requiredP.className = "hint";
    requiredP.append("Required ", duskAmountNode(required));
    row.append(requiredP);

    const postedP = document.createElement("p");
    postedP.className = "hint";
    postedP.append("Posted ", duskAmountNode(posted));
    row.append(postedP);

    if (walletLux != null) {
      const balP = document.createElement("p");
      balP.className = "hint";
      balP.append("Moonlight wallet ", duskAmountNode(walletLux));
      row.append(balP);
    }

    amountsHost.append(row);
  }

  function paintActions() {
    actionsHost.replaceChildren();
    if (loading || bondErr || cutover) return;
    if (!walletAuthorized(wallet)) {
      actionsHost.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Connect your Moonlight wallet to post or withdraw the bond.",
        })
      );
      return;
    }

    const canPost =
      !busy &&
      required > 0n &&
      posted < required;
    const canWithdraw = !busy && posted > 0n;

    const postHint =
      required > 0n
        ? `Post pulls ${luxToDusk(required)} tDUSK from your Moonlight wallet (plus gas).`
        : "Bond requirement is zero - create skips the bond check.";

    const hint = document.createElement("p");
    hint.className = "hint";
    hint.textContent = postHint;
    actionsHost.append(hint);

    actionsHost.append(
      ActionBar([
        Button({
          label: busy ? "…" : "Post bond",
          variant: "primary",
          disabled: !canPost,
          onClick: () => runBondWrite("post"),
        }),
        Button({
          label: busy ? "…" : "Withdraw bond",
          disabled: !canWithdraw,
          onClick: () => runBondWrite("withdraw"),
        }),
      ])
    );
  }

  async function refreshWalletBalance() {
    try {
      const w = getWallet();
      if (!w?.state?.selectedAddress) return;
      const { value } = await w.getPublicBalance();
      walletLux = BigInt(value);
    } catch {
      /* keep last */
    }
  }

  async function refreshBondState() {
    loading = true;
    bondErr = null;
    cutover = false;
    paintAmounts();
    paintActions();

    if (!walletAuthorized(wallet)) {
      loading = false;
      paintAmounts();
      paintActions();
      return;
    }

    try {
      const pkBytes = moonlightPkBytesFromWallet(wallet);
      if (!pkBytes) {
        loading = false;
        paintAmounts();
        paintActions();
        return;
      }
      const dusk = await getChainApp();
      const state = await readParticipationBondState(dusk, pkBytes);
      required = state.required;
      posted = state.posted;
      await refreshWalletBalance();
    } catch (e) {
      bondErr = bondUnavailableMessage(e);
      cutover = bondErr === BOND_CUTOVER_MSG;
    } finally {
      loading = false;
      paintAmounts();
      paintActions();
    }
  }

  async function runBondWrite(kind) {
    if (busy) return;
    busy = true;
    feedback.replaceChildren();
    paintActions();
    let ui;
    try {
      const pkBytes = moonlightPkBytesFromWallet(wallet);
      if (!pkBytes) throw new Error("Connect your Moonlight wallet first");

      const label =
        kind === "post" ? "Post participation bond" : "Withdraw participation bond";
      ui = showGlobalTx({ label });
      const dusk = await getChainApp();

      const tx =
        kind === "post"
          ? await dusk.writeContract({
              contract: "predictionMarket",
              functionName: "post_participation_bond",
              privacy: "public",
              deposit: String(required),
            })
          : await dusk.writeContract({
              contract: "predictionMarket",
              functionName: "withdraw_participation_bond",
              privacy: "public",
            });

      const tracked = await trackTx(tx, { ui, autoDismissMs: 10_000 });
      log?.(`${kind} participation bond tx ${tracked.hash}`);
      await refreshBondState();
    } catch (e) {
      const msg = bondUnavailableMessage(e);
      feedback.replaceChildren(ErrorBanner({ message: msg }));
      log?.(`${kind} participation bond failed: ${msg}`);
      ui?.setStatus("failed", msg);
    } finally {
      busy = false;
      paintActions();
    }
  }

  void refreshBondState();
  return sec;
}

/**
 * Account / settings page - section rail + explainers.
 * Backend URLs are intentionally not shown (code defaults; deploy-time config).
 *
 * @param {{
 *   wallet: object,
 *   log?: Function,
 *   onChange?: Function,
 *   section?: string,
 * }} opts
 */
export function renderSettingsPage({
  wallet,
  log,
  onChange,
  section = DEFAULT_SETTINGS_SECTION,
}) {
  const active = SETTINGS_SECTIONS.includes(section)
    ? section
    : DEFAULT_SETTINGS_SECTION;

  const root = document.createElement("div");
  root.className = "settings-page";

  const chips = document.createElement("div");
  chips.className = "settings-section-chips";
  chips.setAttribute("aria-label", "Settings sections");

  const layout = document.createElement("div");
  layout.className = "settings-layout";

  const nav = document.createElement("nav");
  nav.className = "settings-rail";
  nav.setAttribute("aria-label", "Settings sections");

  for (const id of SETTINGS_SECTIONS) {
    const label = settingsSectionLabel(id);

    const railBtn = document.createElement("button");
    railBtn.type = "button";
    railBtn.className = "settings-rail-item";
    if (id === active) railBtn.classList.add("is-active");
    railBtn.textContent = label;
    railBtn.addEventListener("click", () => {
      location.hash = settingsHash(id);
    });
    nav.append(railBtn);

    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "settings-chip";
    if (id === active) chip.classList.add("is-active");
    chip.textContent = label;
    chip.addEventListener("click", () => {
      location.hash = settingsHash(id);
    });
    chips.append(chip);
  }

  const main = document.createElement("div");
  main.className = "settings-main";

  const explainer = SETTINGS_EXPLAINERS[active];
  const titleWrap = document.createElement("header");
  titleWrap.className = "page-header settings-page-header";
  const title = document.createElement("h1");
  title.textContent = explainer?.title || settingsSectionLabel(active);
  titleWrap.append(title);
  if (explainer?.body) {
    titleWrap.append(
      Object.assign(document.createElement("p"), {
        className: "settings-section-explainer hint",
        textContent: explainer.body,
      })
    );
  }
  main.append(titleWrap);

  const body = document.createElement("div");
  body.className = "settings-section-body";

  if (active === "account") {
    const statusSec = document.createElement("section");
    statusSec.className = "settings-section dt-card";
    const statusH = document.createElement("h2");
    statusH.textContent = "Status";
    statusSec.append(statusH);

    const statusHost = document.createElement("div");
    statusHost.className = "settings-status-host";
    statusSec.append(statusHost);
    let ready = paintStatus(statusHost);
    body.append(statusSec);

    const setupSec = document.createElement("section");
    setupSec.className = "settings-section dt-card";
    const setupH = document.createElement("h2");
    setupH.textContent = ready ? "Setup (completed)" : "Setup";
    setupSec.append(setupH);

    const panel = renderParticipantPanel({
      wallet,
      log,
      onChange: () => {
        ready = paintStatus(statusHost);
        setupH.textContent = ready ? "Setup (completed)" : "Setup";
        onChange?.();
      },
    });

    if (ready) {
      const details = document.createElement("details");
      details.className = "settings-completed";
      const summary = document.createElement("summary");
      summary.textContent = "Show setup tools";
      details.append(summary);
      details.append(panel);
      setupSec.append(details);
    } else {
      setupSec.append(panel);
    }
    body.append(setupSec);
  } else if (active === "security") {
    body.append(
      renderSettingsSecurityPanel({
        onOpenAccount: () => {
          location.hash = settingsHash("account");
        },
      })
    );
  } else if (active === "creator") {
    body.append(renderParticipationBondSection({ wallet, log }));
  } else if (active === "preferences") {
    const prefsSec = document.createElement("section");
    prefsSec.className = "settings-section dt-card";

    const retryStateBadge = document.createElement("div");
    retryStateBadge.className = "state-row";
    function paintRetryBadge(enabled) {
      retryStateBadge.replaceChildren(
        Badge({
          variant: enabled ? "success" : "neutral",
          label: enabled ? "Auto-retry: on" : "Auto-retry: off",
        })
      );
    }
    paintRetryBadge(isAutoRetryEnabled());
    prefsSec.append(retryStateBadge);

    prefsSec.append(
      Checkbox({
        id: "settings-auto-retry-nonce",
        label: "Retry once if a nonce went stale (another tab or device).",
        checked: isAutoRetryEnabled(),
        onChange: (checked) => {
          setAutoRetryEnabled(checked);
          paintRetryBadge(checked);
          log?.(`auto-retry on stale nonce: ${checked ? "enabled" : "disabled"}`);
        },
      })
    );
    prefsSec.append(
      Object.assign(document.createElement("p"), {
        className: "hint",
        textContent:
          "Applies to trade, deposit, withdraw, dispute, and claim. Off by default. A stale nonce only happens from a race with another tab or device (this page already avoids racing itself). A failed attempt does not spend a nonce or funds. When off, a stale-nonce failure just tells you to try again.",
      })
    );
    body.append(prefsSec);
  }

  main.append(body);
  layout.append(nav, main);
  root.append(chips, layout);
  return root;
}
